import { useRef, useState, useEffect, useCallback, createContext, PropsWithChildren } from 'react';

import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useErrorBoundary } from 'react-error-boundary';

import { sendFEEvent } from '../eventUtil';
import { useAPI } from '../services/api.service';
import { updateAxiosSessionToken } from '../apiUtil';
import { useAppConfig } from '../hooks/use-app-config';
import { mobileSDKUtils } from '../utils/mobile-sdk-utils';
import { logPerformance } from '../utils/performance-utils';
import { ICheckoutSummaryContext } from './summary-provider-utils';
import { getCardPaymentMethod } from '../utils/get-card-payment-method';
import { getIsSupercellTemplate } from '../utils/get-is-supercell-template';
import { Offer, ESdkEvents, EventParams, PaymentMethod } from '../contracts';
import { usePaymentState } from '../components/Checkout/hooks/use-payment-state';
import { getFirstPriorityPaymentMethod } from '../utils/get-first-priority-payment-method';
import { updateRumCustomerData, logInfo } from '../utils/coralogix-rum';

export const CheckoutSummaryContext = createContext<ICheckoutSummaryContext | null>(null);

const IS_SUPERCELL_TEMPLATE = getIsSupercellTemplate();

export function CheckoutSummaryProvider({ children }: PropsWithChildren) {
  const { showBoundary } = useErrorBoundary();
  const { updateFeatureFlags } = useAppConfig();
  const [checkoutData, setCheckoutData] = useState<Offer>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [sessionToken, setSessionToken] = useState('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod>();
  const isOrderCreatedEventSentRef = useRef(false);
  const selectedPaymentMethodDisplayNameRef = useRef('');
  const [safeCharge, setSafeCharge] = useState<any>(null);
  const location = useLocation();
  const setErrorMessage = usePaymentState((state) => state.setErrorMessage);
  const setIsApmLoading = usePaymentState((state) => state.setIsApmLoading);
  const setPriceBreakdown = usePaymentState((state) => state.setPriceBreakdown);
  const setZipCode = usePaymentState((state) => state.setZipCode);
  const setFullAddress = usePaymentState((state) => state.setFullAddress);
  const promoCodeMetadata = usePaymentState((state) => state.promoCodeMetadata);
  const setSavedCards = usePaymentState((state) => state.setSavedCards);
  const setCustomerGeoState = usePaymentState((state) => state.setCustomerGeoState);
  const setEmail = usePaymentState((state) => state.setEmail);
  const { t } = useTranslation(undefined, { keyPrefix: 'checkout' });
  const { API } = useAPI();
  const isSupercellCacheNavigationCheckCompletedRef = useRef(false);

  const [redirectUrl, setRedirectUrl] = useState(() => {
    const redirectUrl = new URLSearchParams(location.search).get('redirectUrl');
    return redirectUrl || '';
  });
  const [disableRedirect, setDisableRedirect] = useState(false);

  const fetchData = useCallback(
    async (sessionToken: string, sdkVersion: string, sourceVersion: string) => {
      try {
        const result = await API.getCheckoutSummary(sourceVersion, sdkVersion);
        logPerformance('getCheckoutSummary');
        setCheckoutData(result);
        updateFeatureFlags(result.checkoutConfigurations.featureFlags);

        setPriceBreakdown(result.pricing.priceBreakdown);
        setZipCode(result.customer.address?.zipCode || '');
        setFullAddress(result.customer.address?.address || '');

        setSavedCards(result.savedCards || []);
        setEmail(result.customer.email || '');

        // Update Coralogix RUM with customer data
        updateRumCustomerData(sessionToken, result.customer.email);
        // If for any reason there is no redirectUrl
        // don't override the value in state and local storage.
        // There might be a value that was previously fetched from
        // search params (see the relevant useEffect below)
        // The initial state is an empty string, so it won't go undefined anyway
        if (result.redirectUrl) {
          setRedirectUrl(result.redirectUrl);
          localStorage.setItem('redirectUrl', result.redirectUrl);
        }

        const initialApm = mobileSDKUtils.isMobileSdk()
          ? getCardPaymentMethod(result.paymentMethods)
          : getFirstPriorityPaymentMethod(result.paymentMethods, result.checkoutConfigurations);
        if (!initialApm) {
          showBoundary('');
          sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
            sessionId: sessionToken,
            error: {
              isFatal: true,
            },
          });
        }
        setSelectedPaymentMethod(initialApm);
        selectedPaymentMethodDisplayNameRef.current = initialApm?.paymentMethodDisplayName || '';

        try {
          logInfo('Payment methods loaded', {
            totalPaymentMethods: result.paymentMethods?.length || 0,
            paymentMethods: result.paymentMethods?.map((pm) => ({
              id: pm.id,
              name: pm.paymentMethodName,
              displayName: pm.paymentMethodDisplayName,
              provider: pm.provider,
            })),
            selectedPaymentMethod: {
              id: initialApm?.id,
              name: initialApm?.paymentMethodName,
              displayName: initialApm?.paymentMethodDisplayName,
              provider: initialApm?.provider,
            },
            redirectUrl: result.redirectUrl || null,
            isMobileSDK: mobileSDKUtils.isMobileSdk(),
          });
        } catch (error) {
          console.error('Error logging payment methods:', error);
        }

        // initialize nuvei
        const nuveiConfig = result?.pks?.nuvei;
        if (nuveiConfig) {
          const safeCharge = window.SafeCharge?.({
            env: nuveiConfig.env,
            merchantId: nuveiConfig.merchantId,
            merchantSiteId: nuveiConfig.merchantSiteId,
          });
          setSafeCharge(safeCharge);
        }
      } catch (error: any) {
        if (error?.response?.data?.redirectUrl && !disableRedirect) {
          localStorage.setItem('redirectUrl', error.response.data.redirectUrl);
        }
        const timestamp = new Date().toString();
        sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
          date: timestamp,
          sessionId: sessionToken,
          error: {
            isFatal: true,
          },
        });
        console.error('Error fetching data:', error);
        showBoundary(error?.response?.data?.message);
      } finally {
        setIsLoading(false);
      }
    },
    [API.getCheckoutSummary, updateFeatureFlags]
  );

  const getEventParams = useCallback(() => {
    const timestamp = Date.now();
    return {
      date: timestamp.toString(),
      sessionId: sessionToken,
      bundleName: checkoutData?.offer?.name,
      bundleId: checkoutData?.offer?.sku,
      bundleSKU: checkoutData?.offer?.sku,
      products: checkoutData?.items?.map((item) => ({
        name: item.name,
        sku: item.sku,
        amount: item.quantity,
      })),
      totalSum: checkoutData?.pricing?.priceBreakdown?.priceTotal,
      totalSumCurrency: checkoutData?.pricing?.currencyCode,
      userId: checkoutData?.customer?.id,
      userCountry: checkoutData?.customer?.geoLocator?.countryCode2,
      paymentMethodName: selectedPaymentMethodDisplayNameRef.current,
      orderId: checkoutData?.orderId || '',
      promoCode: promoCodeMetadata,
      isSavedPaymentMethodUsed: false,
      isNewPaymentMethodSaved: false,
    } as EventParams;
  }, [checkoutData, sessionToken, promoCodeMetadata]);

  useEffect(() => {
    if (checkoutData && !isOrderCreatedEventSentRef.current) {
      isOrderCreatedEventSentRef.current = true;
      sendFEEvent(ESdkEvents.ORDER_CREATED, { ...getEventParams() });
    }
  }, [checkoutData, sessionToken, getEventParams]);

  useEffect(() => {
    const token = location.pathname.slice(1);

    if (token === 'handshake') {
      // temporary fix because of the 403 that we get from the init handshake call, we need to use routing instead so we wont reach this point at all
      return;
    }

    const searchParams = new URLSearchParams(location.search);
    const sdkVersion = searchParams.get('sdk-version') || '';
    const sourceVersion = searchParams.get('source-version') || '';
    const disableRedirect = searchParams.get('redirect') === 'false';
    setDisableRedirect(disableRedirect);

    setSessionToken(token);
    if (token) {
      updateAxiosSessionToken(token).then(() => {
        fetchData(token, sdkVersion, sourceVersion);
      });
    }
  }, [fetchData]);

  const onPaymentMethodChange = (pm: PaymentMethod) => {
    if (pm.id === selectedPaymentMethod?.id) {
      return;
    }
    selectedPaymentMethodDisplayNameRef.current = pm.paymentMethodDisplayName;
    setIsApmLoading(true);
    setSelectedPaymentMethod(pm);
    setErrorMessage('');
  };

  useEffect(() => {
    const handleUnload = () => {};
    window.addEventListener('unload', handleUnload);
    return () => {
      window.removeEventListener('unload', handleUnload);
    };
  }, []);

  if (IS_SUPERCELL_TEMPLATE && !isSupercellCacheNavigationCheckCompletedRef.current) {
    const nav = performance.getEntriesByType(
      'navigation'
    )?.[0] as PerformanceResourceTimingExperimental;
    console.log('nav', { nav });

    if (nav?.type === 'back_forward' && nav.deliveryType === 'cache') {
      sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
        error: {
          isFatal: true,
        },
      });
      window.location.reload();
    }
    isSupercellCacheNavigationCheckCompletedRef.current = true;
  }

  return (
    <CheckoutSummaryContext.Provider
      value={{
        checkoutData,
        getEventParams,
        selectedPaymentMethod,
        onPaymentMethodChange,
        isCheckoutDataLoading: isLoading,
        safeCharge,
        selectedPaymentMethodDisplayNameRef,
        redirectUrl,
        disableRedirect,
      }}
    >
      {children}
    </CheckoutSummaryContext.Provider>
  );
}

// workaround for ACDEV-18889 - when coming from cache we get unexpected behavior
type PerformanceResourceTimingExperimental = PerformanceNavigationTiming & {
  deliveryType: 'cache';
};
